import React, { useState } from 'react';
import { Eye, EyeOff, Copy, RefreshCw, Code, BookOpen, FileCode, Server, Lightbulb, Brain, Key } from 'lucide-react';
import {Button} from "@/components/shadcn/button.tsx";
import {Icon} from "@/components/Icon.tsx";
import {useNavigate} from "react-router-dom";

const HomePage = () => {
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKey] = useState('Rw4c**********************************2e51');
  const navigate = useNavigate();

  const copyApiKey = () => {
    navigator.clipboard.writeText('Rw4cyLNOIKEq2fa3927cd977217774d23f054b2e51');
  };

  const toggleApiKeyVisibility = () => {
    setApiKeyVisible(!apiKeyVisible);
  };

  const changeApiKey = () => {
    // Implement API key change logic
    console.log('Change API key');
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="!max-w-7xl w-full">
        <div className="w-full">
          {/* Header Section */}
          <section className="text-center mb-8">
            <h1 className="text-4xl font-bold tracking-tight mb-4 text-foreground">
              Welcome to BrowserCloud
            </h1>
            <p className="text-muted-foreground max-w-4xl mx-auto">
              The intelligent automation platform built for scalability, reliability, and seamless integration—choose the solution that fits your needs.
            </p>
          </section>

          {/* API Key Section */}
          <div className="rounded-xl border bg-card text-card-foreground shadow w-full flex flex-col h-full">
            <div className="flex flex-col space-y-1.5 p-6">
              <div className="font-semibold leading-none tracking-tight">Your API Key</div>
            </div>
            <div className="p-6 pt-0">
              <p className="text-muted-foreground mb-2">
                Use this API key to authenticate with all BrowserCloud.io services. Include this token as a{' '}
                <code className="bg-muted px-1 rounded">?token=YOUR_TOKEN</code>{' '}
                parameter in all API requests.
              </p>
              <div className="space-y-4">
                <div className="flex flex-col space-y-2">
                  <div className="flex gap-4 items-center">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Key className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col">
                        <input
                          id="api-key-input"
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm pl-10 pr-24 font-mono dark:border-border"
                          readOnly
                          type={apiKeyVisible ? 'text' : 'password'}
                          value={apiKeyVisible ? 'Rw4cyLNOIKEq2fa3927cd977217774d23f054b2e51' : apiKey}
                        />
                      </div>
                      <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                        <button
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-9 w-9"
                          title="Show API Key"
                          onClick={toggleApiKeyVisibility}
                        >
                          {apiKeyVisible ? <EyeOff /> : <Eye />}
                        </button>
                        <button
                          className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground h-8 rounded-md px-3 text-xs"
                          title="Copy API Key"
                          onClick={copyApiKey}
                        >
                          <Copy />
                        </button>
                      </div>
                    </div>
                    <button
                      className="justify-center gap-2 font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground dark:!bg-card dark:hover:!bg-accent h-8 rounded-md px-3 text-xs flex items-center whitespace-nowrap"
                      onClick={changeApiKey}
                    >
                      <RefreshCw />
                      Change API Key
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Chat with BrowserCloud AI Button */}
          <div className="my-10 flex flex-col items-center">
            <Button onClick={() => {navigate("/chat")}} className="[&_svg]:size-6">
              <Icon icon="fluent:bot-sparkle-20-regular" />
              Chat with BrowserCloud AI
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Our assistant can answer questions about our product
            </p>
          </div>

          {/* Services Grid */}
          <section>
            <div className="grid grid-cols-2 gap-8 md:grid-cols-1">
              <div className="flex gap-4">

                {/* Frameworks */}
                <div className="rounded-xl border bg-card text-card-foreground shadow w-full flex flex-col h-full">
                  <div className="flex flex-col space-y-1.5 p-6">
                    <div className="flex items-center gap-2">
                      <Icon icon="fluent:plug-disconnected-20-regular" height={32} />
                      <div className="font-semibold tracking-tight text-2xl">Frameworks: Playwright, Puppeteer or another </div>
                    </div>
                  </div>
                  <div className="p-6 pt-0 flex-grow">
                    <p>
                      Connect your browser automation to cloud browsers using Puppeteer, Playwright and other popular libraries base on CDP (Chrome Debugging Protocol)
                    </p>
                  </div>
                  <div className="items-center p-6 flex flex-col gap-4 border-t dark:border-border py-4 mt-auto">
                    <div className="flex gap-4 items-start justify-start w-full">
                      <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                        <Code />
                        Code Snippet
                      </button>
                      <a href="/chat" target="_blank" rel="noopener noreferrer">
                        <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                          <FileCode />
                          Chat Interface
                        </button>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border bg-card text-card-foreground shadow w-full flex flex-col h-full">
                  <div className="flex flex-col space-y-1.5 p-6">
                    <div className="flex items-center gap-2">
                      <Icon icon="fluent:code-block-24-regular" height={32} />
                      <div className="font-semibold tracking-tight text-2xl">HTTP API</div>
                    </div>
                  </div>
                  <div className="p-6 pt-0 flex-grow">
                    <p>
                      HTTP API endpoints for taking screenshots, generating PDFs, extracting content, and more. Great for integration with any programming language or automation tools like Zapier, Make, n8n
                    </p>
                  </div>
                  <div className="items-center p-6 flex flex-col gap-4 border-t dark:border-border py-4 mt-auto">
                    <div className="flex gap-4 items-start justify-start w-full">
                      <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                        <Code />
                        Code Snippet
                      </button>
                      <a href="/dashboard" target="_blank" rel="noopener noreferrer">
                        <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                          <BookOpen />
                          Dashboard
                        </button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4">
                {/* API Integration */}
                <div className="rounded-xl border bg-card text-card-foreground shadow w-full flex flex-col h-full">
                  <div className="flex flex-col space-y-1.5 p-6">
                    <div className="flex items-center gap-2">
                      <Icon icon="fluent:server-play-20-regular" height={32} />
                      <div className="font-semibold tracking-tight text-2xl">Cloud Workers</div>
                    </div>
                  </div>
                  <div className="p-6 pt-0 flex-grow">
                    <p>
                      Workers allow you to execute custom JavaScript code in a browser context using the simplest HTTP API endpoints. This is useful for: Multi-step workflows, AI capabilities for your apps.
                    </p>
                  </div>
                  <div className="items-center p-6 flex flex-col gap-4 border-t dark:border-border py-4 mt-auto">
                    <div className="flex gap-4 items-start justify-start w-full">
                      <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                        <Code />
                        Code Snippet
                      </button>
                      <a href="/profile" target="_blank" rel="noopener noreferrer">
                        <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                          <BookOpen />
                          API Docs
                        </button>
                      </a>
                    </div>
                  </div>
                </div>

                {/* AI Automation */}
                <div className="rounded-xl border bg-card text-card-foreground shadow w-full flex flex-col h-full">
                  <div className="flex flex-col space-y-1.5 p-6">
                    <div className="flex items-center gap-2">
                      <Icon icon="hugeicons:ai-chip" height={32} />
                      <div className="font-semibold tracking-tight text-2xl">AI Automation</div>
                    </div>
                  </div>
                  <div className="p-6 pt-0 flex-grow">
                    <p>
                      Integrate BrowserCloud with popular AI frameworks such as n8n, browser-use and vercel ai sdk to enable web browsing capabilities in your AI applications.
                    </p>
                  </div>
                  <div className="items-center p-6 flex flex-col gap-4 border-t dark:border-border py-4 mt-auto">
                    <div className="flex gap-4 items-start justify-start w-full">
                      <a href="/profile" target="_blank" rel="noopener noreferrer">
                        <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 text-muted-foreground hover:text-primary transition-colors !p-0 h-9 px-4 py-2">
                          <BookOpen />
                          Automation Docs
                        </button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;